import { useCallback, useEffect, useState } from 'react';

import api from './api';

/**
 * 건강 뉴스(카드뉴스) 조회 — POST content/readAllCardNews
 *
 * 홈(MainCare)과 케어(MainNews)가 같은 블록을 각각 복사해 갖고 있었다. 같은 버그가
 * 두 벌 있었으므로 조회 규칙을 여기 하나로 모은다. 화면은 목록만 받아 그리면 된다.
 *
 * 응답: { count: 전체 건수, cardNewsList: [{ title, thumbNail, images, hashTags }] }
 * 요청: { page } — 0부터 시작, 한 페이지 10건
 */

export type CardNews = {
  title: string;
  thumbNail: string;
  images: string[];
  hashTags: string[];
};

/** 서버 한 페이지 크기 */
const PAGE_SIZE = 10;

/**
 * 카드뉴스 이미지 URL.
 * 'aws' 가 들어 있으면 이미 절대 경로이고, 아니면 파일명이라 저장소 경로를 붙인다.
 *
 * 빈 값에 undefined 를 돌려주는 이유: 그대로 두면 디렉터리 URL 을 이미지로 요청하게 된다.
 * React 는 src 가 undefined 면 속성 자체를 넣지 않아 요청이 나가지 않는다.
 */
export const cardNewsImageUrl = (name: string | null | undefined): string | undefined => {
  if (!name) return undefined;
  return name.includes('aws') ? name : `https://file.dualhealth.kr/healthContent/cardNews/${name}`;
};

/**
 * 화면에 그릴 수 있는 항목인지.
 *
 * 제목이 빈 항목을 거른다 — 화면들이 `useState([{ title: '', ... }])` 로 빈 더미를 하나
 * 깔아두고 응답을 concat 만 해서, 목록에 빈 카드가 계속 남아 있었다(눌러도 빈 모달이 열렸다).
 * 서버가 제목 없는 레코드를 주는 경우도 같이 걸린다.
 */
const isRenderable = (item: any): item is CardNews =>
  item !== null && item !== undefined && typeof item.title === 'string' && item.title !== '';

/**
 * Fisher–Yates 셔플.
 *
 * 기존 `sort(() => Math.random() - 0.5)` 는 비교자가 같은 쌍에 매번 다른 답을 주는
 * 비일관 비교자라 결과가 균등하지 않다(앞쪽 원소가 앞에 남는 편향이 생긴다).
 */
const shuffled = <T>(list: T[]): T[] => {
  const out = [...list];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
};

const listOf = (res: any): CardNews[] => {
  const list = res?.data?.cardNewsList;
  return Array.isArray(list) ? list.filter(isRenderable) : [];
};

/**
 * 전체 건수 → 페이지 수.
 *
 * 기존 코드는 `Math.floor(count / 10)` 이었다. 10건 미만이면 0 이 되어 조회 루프가
 * 한 번도 돌지 않았고(뉴스가 하나도 안 나왔다), 25건이면 2 가 되어 마지막 5건이 빠졌다.
 * count 가 없거나 이상한 값이면 1페이지로 본다.
 */
export const pageCountOf = (count: unknown): number => {
  const n = typeof count === 'number' ? count : Number(count);
  if (!Number.isFinite(n) || n <= 0) return 1;
  return Math.max(1, Math.ceil(n / PAGE_SIZE));
};

export type UseCardNews = {
  items: CardNews[];
  loading: boolean;
  /** 조회 실패 — 화면은 안내와 재시도 수단을 보여준다 */
  error: boolean;
  reload: () => void;
};

/**
 * @param limit 화면에 그릴 카드 수
 *
 * 요청은 최대 2번이다. 예전에는 `count / 10` 개를 한꺼번에 던졌는데(뉴스 200건이면 20개),
 * 정작 화면은 5장만 쓴다. '매번 다른 뉴스를 보여준다'는 의도는 전체를 받아 섞는 대신
 * **무작위 페이지 한 장**을 받는 것으로 유지한다.
 *
 * 목록은 다 받은 뒤 한 번만 정한다. 예전에는 페이지 응답이 도착할 때마다 전체를 다시
 * 섞어서, 사용자가 보고 있는 중에 카드 순서가 몇 번씩 바뀌었다.
 */
export const useCardNews = (limit: number): UseCardNews => {
  const [items, setItems] = useState<CardNews[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    // 화면을 빠르게 벗어나면 응답이 늦게 도착한다 — 언마운트 뒤에는 반영하지 않는다
    let alive = true;
    setLoading(true);
    setError(false);

    (async () => {
      try {
        // 첫 페이지는 전체 건수를 알기 위해서도 필요하므로 목록까지 함께 쓴다
        const first = await api.post('content/readAllCardNews', { page: 0 });
        if (!alive) return;

        const firstList = listOf(first);
        const pages = pageCountOf(first?.data?.count);
        const page = pages <= 1 ? 0 : Math.floor(Math.random() * pages);

        if (page === 0) {
          setItems(shuffled(firstList).slice(0, limit));
          return;
        }

        const picked = await api.post('content/readAllCardNews', { page });
        if (!alive) return;

        // 고른 페이지가 비어 있으면(count 와 실제 건수가 어긋난 경우) 첫 페이지로 물러난다
        const pickedList = listOf(picked);
        setItems(shuffled(pickedList.length > 0 ? pickedList : firstList).slice(0, limit));
      } catch (e) {
        if (!alive) return;
        console.log(e);
        setItems([]);
        setError(true);
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [limit, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  return { items, loading, error, reload };
};
